/**
 * Asset Tag Modal Component
 *
 * Modal for adding tags to assets with optimistic updates and
 * duplicate prevention.
 *
 * Features:
 * - Optimistic updates for instant UI feedback
 * - Duplicate tag prevention (case-insensitive)
 * - Granular error reporting per asset
 * - Automatic cache invalidation and refresh
 *
 * Improvements aligned with vulnerability implementation:
 * - Uses optimistic updates for better UX
 * - Prevents duplicate tags
 * - Better error handling with detailed feedback
 */

import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import { queryClient } from '@/queryclient';
import { AddTag } from '@/sections/AddTag';
import { useMutation } from '@/utils/api';
import {
  tagAddedError,
  tagAddedSuccess,
  tagValidationError,
} from '@/utils/tagToastMessages';
import { validateTagInput } from '@/utils/tagValidation';

interface Props {
  open: boolean;
  onClose: () => void;
  assetKeys: string[];
  currentTags?: string[]; // Deprecated: No longer used, always fetches from backend
  onSuccess?: () => void;
}

interface TagOperationResult {
  succeeded: number;
  failed: number;
  duplicates: number;
  results: Array<{ key: string; skipped?: boolean; reason?: string }>;
}

/**
 * Modal component for adding tags to assets
 *
 * Uses shared AddTag component for consistent UX across the platform.
 * Handles both single asset (from drawer) and bulk operations (from table).
 *
 * Note: Always fetches current tags from backend to prevent data loss.
 * The currentTags prop is deprecated and no longer used.
 *
 * Example usage:
 * ```tsx
 * <AssetTag
 *   open={modalState.tagModal}
 *   onClose={modalHandlers.closeTagModal}
 *   assetKeys={selectedKeys}
 *   onSuccess={clearSelection}
 * />
 * ```
 *
 * @param props - Component props
 * @returns Modal component
 */
export const AssetTag = ({
  open,
  onClose,
  assetKeys,
  currentTags: _currentTags = [], // Deprecated: No longer used, always fetches from backend
  onSuccess,
}: Props) => {
  const axios = useAxios();

  const { mutate: updateAssets, status: updatingAssets } = useMutation<
    TagOperationResult,
    Error,
    string
  >({
    defaultErrorMessage: 'Failed to add tag',
    mutationFn: async (newTag: string) => {
      // Fetch current state for all assets
      const promises = assetKeys.map(async assetKey => {
        try {
          // ALWAYS fetch current asset to get both tags AND status
          // Backend requires status to be included when updating tags
          const currentAsset = await axios.get(`/my`, {
            params: {
              label: 'asset',
              key: assetKey,
              exact: true,
            },
          });

          const asset = currentAsset.data.assets?.[0];
          if (!asset) {
            throw new Error(`Asset not found: ${assetKey}`);
          }

          const existingTags = asset.tags || [];
          const currentStatus = asset.status || 'A';

          // Check for duplicate (case-insensitive)
          const isDuplicate = existingTags.some(
            (tag: string) => tag.toLowerCase() === newTag.toLowerCase()
          );

          if (isDuplicate) {
            return {
              key: assetKey,
              skipped: true,
              reason: 'Tag already exists',
            };
          }

          const payload = {
            key: assetKey,
            status: currentStatus, // Include current status (required by backend)
            tags: [...existingTags, newTag],
          };

          await axios.put('/asset', payload);

          return {
            key: assetKey,
            skipped: false,
          };
        } catch (error) {
          console.error(`[AssetTagModal] Failed for ${assetKey}:`, error);
          throw error;
        }
      });

      const results = await Promise.allSettled(promises);

      // Process results
      const succeeded = results.filter(
        r => r.status === 'fulfilled' && !r.value.skipped
      ).length;

      const duplicates = results.filter(
        r => r.status === 'fulfilled' && r.value.skipped
      ).length;

      const failed = results.filter(r => r.status === 'rejected').length;

      // Log failures for debugging
      if (failed > 0) {
        const failedResults = results
          .filter((r): r is PromiseRejectedResult => r.status === 'rejected')
          .map((r, i) => ({
            key: assetKeys[i],
            error: r.reason?.message || 'Unknown error',
          }));
        console.error('[AssetTagModal] Failed operations:', failedResults);
      }

      return {
        succeeded,
        duplicates,
        failed,
        results: results.map((r, i) => {
          if (r.status === 'fulfilled') {
            return r.value;
          }
          return { key: assetKeys[i], skipped: false };
        }),
      };
    },
    onMutate: async newTag => {
      // Cancel outgoing refetches to prevent overwriting optimistic update
      await queryClient.cancelQueries({
        predicate: query => {
          const queryKey = query.queryKey as string[];
          return queryKey[0] === 'MY' && queryKey[1] === 'asset';
        },
      });

      // Snapshot previous value for rollback
      const previousData = queryClient.getQueriesData({
        predicate: query => {
          const queryKey = query.queryKey as string[];
          return queryKey[0] === 'MY' && queryKey[1] === 'asset';
        },
      });

      // Optimistically update all asset queries
      queryClient.setQueriesData(
        {
          predicate: query => {
            const queryKey = query.queryKey as string[];
            return queryKey[0] === 'MY' && queryKey[1] === 'asset';
          },
        },
        (old: any) => {
          if (!old?.assets) return old;

          return {
            ...old,
            assets: old.assets.map((asset: any) => {
              if (assetKeys.includes(asset.key)) {
                const existingTags = asset.tags || [];
                // Only add if not duplicate (case-insensitive)
                const isDuplicate = existingTags.some(
                  (tag: string) => tag.toLowerCase() === newTag.toLowerCase()
                );
                if (isDuplicate) return asset;

                return {
                  ...asset,
                  tags: [...existingTags, newTag],
                };
              }
              return asset;
            }),
          };
        }
      );

      return { previousData };
    },
    onError: (_err: Error, _newTag: string, context: unknown) => {
      const contextData = context as
        | { previousData?: Array<[any, any]> }
        | undefined;
      // Rollback on error
      if (contextData?.previousData) {
        contextData.previousData.forEach(([queryKey, data]: [any, any]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      toast.error('Failed to add tag. Changes have been reverted.');
    },
    onSettled: async () => {
      // Invalidate queries after mutation completes
      // Use predicate-based invalidation to catch ALL asset queries
      // This matches the pattern from useUpdateSubscription
      // NOTE: UniqueQueryKeys.MY is 'MY' (uppercase), not 'my'
      await queryClient.invalidateQueries({
        predicate: query => {
          const queryKey = query.queryKey as string[];
          // Invalidate any 'MY' query for 'asset' resource
          return queryKey[0] === 'MY' && queryKey[1] === 'asset';
        },
        refetchType: 'all', // Use 'all' to force refetch of drawer and table
      });
    },
  });

  const handleSubmit = async (tag: string) => {
    // Validate input
    const validation = validateTagInput(tag);
    if (!validation.isValid) {
      toast.error(tagValidationError(validation.error!));
      return;
    }

    try {
      const result = await new Promise<TagOperationResult>(
        (resolve, reject) => {
          updateAssets(validation.sanitized!, {
            onSuccess: resolve,
            onError: reject,
          });
        }
      );

      // Show appropriate feedback based on results
      if (
        result.succeeded > 0 &&
        result.failed === 0 &&
        result.duplicates === 0
      ) {
        const count = result.succeeded > 1 ? result.succeeded : undefined;
        toast.success(tagAddedSuccess('asset', count));
        onSuccess?.();
        onClose();
      } else if (
        result.succeeded > 0 &&
        (result.failed > 0 || result.duplicates > 0)
      ) {
        let message = `Tag added to ${result.succeeded} asset${result.succeeded > 1 ? 's' : ''}`;
        if (result.duplicates > 0) {
          message += `, skipped ${result.duplicates} (already tagged)`;
        }
        if (result.failed > 0) {
          message += `, failed ${result.failed}`;
        }
        toast.warning(message);
        onSuccess?.();
        onClose();
      } else if (result.duplicates === assetKeys.length) {
        toast.warning('All selected assets already have this tag');
      } else {
        toast.error(tagAddedError('asset'));
      }
    } catch (error: any) {
      console.error('[AssetTagModal] Tag operation failed:', error);
      // Error toast already shown in onError
    }
  };

  return (
    <AddTag
      open={open}
      onClose={onClose}
      disabled={updatingAssets === 'pending'}
      onSubmit={handleSubmit}
    />
  );
};
